// Standalone fund investor roster (16.d.3.3). Promotes the /fund embedded
// INVESTORS table to its own top-level page — the manager nav "Investors"
// points here. Fund-scoped: mirrors /fund's fund switcher (default first
// fund) and the same column set + per-investor detail drill-down.
//
// The firm-wide SMA roster (period-close, AUM/MTD, recon health, credentials)
// still lives at /clients, but its utility link here was RETIRED (16.e.5,
// operator: fold) now that fund-investor statements (16.e.3) replaced what
// /clients uniquely offered — the fund-first nav is the only manager surface.
// /clients stays reachable by direct URL + the super-user credential-admin
// back-button (route + Clients.tsx kept on disk, reversible).
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { PageHeader } from '../../components/shell';
import { CreateInvestorForm, type CreatedInvestor } from '../../components/CreateInvestorForm';
import { fmtBase, fmtPct, fmtUnitPriceOps } from '../../lib/format';
import { api } from '../../lib/api';

const fmtUnits = (s: string | number) =>
  Number(s).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 8 });
const fmtDT = (iso: string) => new Date(iso).toISOString().slice(0, 16).replace('T', ' ') + ' UTC';

export default function Investors() {
  const [params, setParams] = useSearchParams();
  const [funds, setFunds] = useState<any[] | null>(null);
  const [fundId, setFundId] = useState<string | null>(params.get('fund_id'));
  const [investors, setInvestors] = useState<any[]>([]);
  const [hwms, setHwms] = useState<any | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // 16.e.1 — add an investor without the one-time /fund/new wizard.
  const [adding, setAdding] = useState(false);
  const [created, setCreated] = useState<CreatedInvestor | null>(null);

  const fund = useMemo(() => funds?.find(f => f.fund_id === fundId) || null, [funds, fundId]);

  useEffect(() => {
    api<any[]>('/ops/funds')
      .then(f => {
        setFunds(f);
        // Default to the first fund — and also fall back to it when the URL's
        // ?fund_id= points at a fund that isn't in this tenant (stale bookmark
        // or a deleted fund), so a foreign id can't strand the page on the
        // detail-fetch error path with no switcher to recover.
        if (f.length && (!fundId || !f.some((x: any) => x.fund_id === fundId))) {
          setFundId(f[0].fund_id);
        }
      })
      .catch(() => setErr('Failed to load funds.'));
  }, []);

  const loadRoster = (fid: string) =>
    Promise.all([
      api<any[]>(`/ops/funds/${fid}/investors`),
      api<any>(`/ops/funds/${fid}/hwms`).catch(() => null),
    ])
      .then(([inv, h]) => { setInvestors(inv); setHwms(h); })
      .catch(() => setErr('Failed to load investors.'));

  useEffect(() => { if (fundId) loadRoster(fundId); }, [fundId]);

  const hwmByInvestor = useMemo(() => {
    const map: Record<string, string> = {};
    (hwms?.rows || []).forEach((r: any) => { map[r.investor_id] = r.hwm_per_unit_base; });
    return map;
  }, [hwms]);

  const selectFund = (id: string) => {
    setFundId(id);
    setParams({ fund_id: id }, { replace: true });
    setAdding(false); setCreated(null);  // don't carry the add-form/banner across funds
  };

  if (err) return <div className="view container"><p className="serif-body" style={{ color: 'var(--oxblood)' }}>{err}</p></div>;
  if (!funds) return <div className="view container"><p className="label-s" style={{ color: 'var(--ink-3)' }}>Loading…</p></div>;
  if (!funds.length) return (
    <div className="view container">
      <PageHeader kicker="INVESTORS" title={<><em>Create</em> your fund.</>} />
      <p className="serif-body" style={{ fontSize: 16, marginTop: 24, maxWidth: '54ch', color: 'var(--ink-2)' }}>
        Investors belong to a fund. Create your fund first, then record subscriptions to
        bring investors onto the register.
      </p>
      <Link to="/fund/new" className="btn" style={{ textDecoration: 'none', marginTop: 28, display: 'inline-block' }}>
        Create your fund <span className="btn-arrow">→</span>
      </Link>
    </div>
  );

  return (
    <div className="view container">
      <PageHeader
        kicker="INVESTORS"
        title={<>Fund <em>investors.</em></>}
        meta={<span className="label-s">{investors.length} INVESTOR{investors.length === 1 ? '' : 'S'}{fund ? ` · AS OF ${fmtDT(fund.as_of)}` : ''}</span>}
      />

      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
        {funds.length > 1 && (
          <label className="label-s" style={{ display: 'inline-block' }}>
            FUND&nbsp;
            <select className="num" value={fundId || ''} onChange={e => selectFund(e.target.value)}>
              {funds.map(f => <option key={f.fund_id} value={f.fund_id}>{f.name}</option>)}
            </select>
          </label>
        )}
        <button type="button" className="btn-link"
                onClick={() => { setCreated(null); setAdding(a => !a); }}>
          {adding ? 'Cancel' : 'Add investor'}
        </button>
        {/* 16.e.5 — the legacy firm-wide /clients (SMA roster) link is retired:
            fund-investor statements (16.e.3) replaced what /clients uniquely
            offered, so the fund-first nav is the only surface. The route +
            Clients.tsx are kept (reachable by direct URL), so this is reversible. */}
      </div>

      {adding && (
        <section style={{ marginTop: 28, maxWidth: '46ch' }}>
          <CreateInvestorForm
            onCreated={(inv) => {
              setAdding(false);
              setCreated(inv);
              if (fundId) loadRoster(fundId);  // harmless refetch (registers on subscribe)
            }}
            onCancel={() => setAdding(false)}
          />
        </section>
      )}

      {created && (
        <p className="serif-body" style={{ marginTop: 24, maxWidth: '60ch', color: 'var(--ink-2)' }}>
          <em>{created.name}</em> is created. Record their first subscription on the{' '}
          <Link to="/fund" style={{ color: 'var(--ink)' }}>Fund page</Link>{' '}
          to bring them onto the register.
        </p>
      )}

      <section style={{ marginTop: 40 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>INVESTOR</th>
              <th data-align="right">UNITS</th>
              <th data-align="right">OWNERSHIP</th>
              <th data-align="right">HWM / UNIT</th>
              <th data-align="right">CONTRIBUTED</th>
              <th data-align="right">VALUE</th>
            </tr>
          </thead>
          <tbody>
            {investors.map(i => (
              <tr key={i.investor_id}>
                <td><Link to={`/fund/${fundId}/investors/${i.investor_id}`} style={{ color: 'var(--ink)' }}>{i.investor_name}</Link></td>
                <td data-align="right" className="num">{fmtUnits(i.units)}</td>
                <td data-align="right" className="num">{fmtPct(Number(i.ownership_pct))}</td>
                <td data-align="right" className="num">{hwmByInvestor[i.investor_id] ? fmtUnitPriceOps(Number(hwmByInvestor[i.investor_id]), fund?.base_currency) : '—'}</td>
                <td data-align="right" className="num">{fmtBase(Number(fund?.base_currency === 'USD' ? i.contributed_usd : i.contributed_base), fund?.base_currency)}</td>
                {/* OB.6 — value is null until a strike covers the investor; show */}
                {/* an em-dash, matching the detail drill-down's pending state.    */}
                <td data-align="right" className="num">{i.valuation_pending ? '—' : fmtBase(Number(fund?.base_currency === 'USD' ? i.value_usd : i.value_base), fund?.base_currency)}</td>
              </tr>
            ))}
            {!investors.length && <tr><td colSpan={6} className="label-s" style={{ color: 'var(--ink-3)' }}>No investors yet.</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}
